import styled from '@emotion/styled';
import type { CSSProperties, FC } from 'react';
import { theme } from '../../theme';

export const SIZE = {
  XSMALL: 'XSmall',
  SMALL: 'Small',
  MEDIUM: 'Medium',
  LARGE: 'Large',
  XLARGE: 'XLarge',
};

export const COLOR = {
  NEUTRAL: 'Neutral',
  INFORMATIVE: 'Informative',
  SUCCESS: 'Success',
  WARNING: 'Warning',
  DANGER: 'Danger',
};
export type BadgeProps = {
  text: string;
  size?: (typeof SIZE)[keyof typeof SIZE];
  color?: (typeof COLOR)[keyof typeof COLOR];
  subtle?: boolean;
  className?: string;
  style?: CSSProperties;
  'data-cy'?: string;
};

const getTextColor = (subtle: boolean, color: (typeof COLOR)[keyof typeof COLOR]) => {
  if (!subtle) {
    return theme.colors.textInverted.primary;
  }
  switch (color) {
    case COLOR.NEUTRAL:
      return theme.colors.text.primary;
    case COLOR.INFORMATIVE:
      return theme.colors.emotionInformative.default;
    case COLOR.SUCCESS:
      return theme.colors.emotionSuccess.default;
    case COLOR.WARNING:
      return theme.colors.emotionWarning.default;
    case COLOR.DANGER:
      return theme.colors.emotionDanger.default;
    default:
      return theme.colors.text.primary;
  }
};

const getBackgroundColor = (subtle: boolean, color: (typeof COLOR)[keyof typeof COLOR]) => {
  if (subtle) {
    switch (color) {
      case COLOR.NEUTRAL:
        return theme.colors.emotionNeutral.background;
      case COLOR.INFORMATIVE:
        return theme.colors.emotionInformative.background;
      case COLOR.SUCCESS:
        return theme.colors.emotionSuccess.background;
      case COLOR.WARNING:
        return theme.colors.emotionWarning.background;
      case COLOR.DANGER:
        return theme.colors.emotionDanger.background;
      default:
        return theme.colors.emotionNeutral.background;
    }
  }
  switch (color) {
    case COLOR.NEUTRAL:
      return theme.colors.emotionNeutral.default;
    case COLOR.INFORMATIVE:
      return theme.colors.emotionInformative.default;
    case COLOR.SUCCESS:
      return theme.colors.emotionSuccess.default;
    case COLOR.WARNING:
      return theme.colors.emotionWarning.default;
    case COLOR.DANGER:
      return theme.colors.emotionDanger.default;
    default:
      return theme.colors.emotionNeutral.default;
  }
};

const getBorderRadius = (size: (typeof SIZE)[keyof typeof SIZE]) => {
  if (size === SIZE.LARGE || size === SIZE.XLARGE) {
    return theme.borderRadius.l;
  }
  return theme.borderRadius.s;
};

const getPadding = (size: (typeof SIZE)[keyof typeof SIZE]) => {
  switch (size) {
    case SIZE.XSMALL:
      return `0 ${theme.spacing.c}`;
    case SIZE.SMALL:
      return `${theme.spacing.b} ${theme.spacing.d}`;
    case SIZE.MEDIUM:
      return `4.5px ${theme.spacing.d}`;
    case SIZE.LARGE:
      return `${theme.spacing.d} ${theme.spacing.e}`;
    case SIZE.XLARGE:
      return `${theme.spacing.d} ${theme.spacing.f}`;
    default:
      return `${theme.spacing.c} ${theme.spacing.d}`;
  }
};

const getTypography = (size: (typeof SIZE)[keyof typeof SIZE]) => {
  switch (size) {
    case SIZE.XSMALL:
      return theme.typography.body.small.regular;
    case SIZE.SMALL:
      return theme.typography.body.small.regular;
    case SIZE.MEDIUM:
      return theme.typography.body.medium.regular;
    case SIZE.LARGE:
      return theme.typography.body.large.regular;
    case SIZE.XLARGE:
      return theme.typography.body.xLarge.regular;
    default:
      return theme.typography.body.medium.regular;
  }
};

const StyledBadge = styled.span<Required<Pick<BadgeProps, 'size' | 'color' | 'subtle'>>>`
  gap: ${theme.spacing.c};
  padding: ${({ size }) => getPadding(size)};
  border-radius: ${({ size }) => getBorderRadius(size)};
  color: ${({ subtle, color }) => getTextColor(subtle, color)};
  background-color: ${({ subtle, color }) => getBackgroundColor(subtle, color)};
  font-family: ${({ size }) => getTypography(size).family};
  font-weight: ${({ size }) => getTypography(size).fontWeight};
  font-size: ${({ size }) => getTypography(size).fontSize};
  line-height: ${({ size }) => getTypography(size).lineHeight};
`;

export const Badge: FC<BadgeProps> = ({
  text,
  size = SIZE.MEDIUM,
  color = COLOR.NEUTRAL,
  subtle = false,
  className,
  style,
  'data-cy': dataCy,
}) => {
  return (
    <StyledBadge size={size} color={color} subtle={subtle} className={className} style={style} data-cy={dataCy}>
      {text}
    </StyledBadge>
  );
};
